/**
 * CAD API Service — response mappers
 *
 * Pure helpers that map snake_case API responses to camelCase domain types.
 */

import {
  CADModel,
  ThicknessAnalysis,
  ThicknessAnalysisProfile,
  ThicknessBatchJob,
  ThicknessConfig,
  ThicknessEvidence,
  ThicknessFaceSummary,
  ThicknessPreset,
  ThicknessRegion,
  ThicknessRegionDisposition,
  ThicknessReview,
  ThicknessReviewStatus,
} from '../types/cad';

export function mapModelResponse(data: any): CADModel {
  return {
    id: data.id,
    filename: data.filename,
    originalFormat: data.original_format,
    status: data.status,
    classification: {
      folderPath: String(data.classification?.folder_path || ''),
      tags: Array.isArray(data.classification?.tags)
        ? data.classification.tags.map(String)
        : [],
      machineModel: String(data.classification?.machine_model || ''),
      managementNumber: String(data.classification?.management_number || ''),
    },
    canEditClassification: Boolean(data.can_edit_classification),
    metadata: data.metadata
      ? {
          boundingBox: data.metadata.bounding_box || { x: 0, y: 0, z: 0 },
          volume: data.metadata.volume,
          surfaceArea: data.metadata.surface_area,
          faceCount: data.metadata.face_count || 0,
          vertexCount: data.metadata.vertex_count || 0,
          edgeCount: data.metadata.edge_count || 0,
          centerOfMass: data.metadata.center_of_mass,
          originalFormat: data.metadata.original_format || '',
          originalFileSizeBytes: data.metadata.original_file_size_bytes || 0,
          glbFileSizeBytes: data.metadata.glb_file_size_bytes || 0,
          triangleCount: data.metadata.triangle_count || 0,
          geometryAnalysis: data.metadata.geometry_analysis
            ? {
                version: data.metadata.geometry_analysis.version,
                completedAt: data.metadata.geometry_analysis.completed_at,
                durationMs: data.metadata.geometry_analysis.duration_ms,
                wallThicknessStatus: data.metadata.geometry_analysis.wall_thickness_status,
                crossSectionStatus: data.metadata.geometry_analysis.cross_section_status,
                meshQualityStatus: data.metadata.geometry_analysis.mesh_quality_status,
                draftRiskStatus: data.metadata.geometry_analysis.draft_risk_status || null,
                analysisScope: data.metadata.geometry_analysis.analysis_scope || null,
                reasons: data.metadata.geometry_analysis.reasons || [],
                unitsAssumed: data.metadata.geometry_analysis.units_assumed || 'mm',
              }
            : null,
          wallThickness: data.metadata.wall_thickness
            ? {
                minMm: data.metadata.wall_thickness.min_mm,
                p05Mm: data.metadata.wall_thickness.p05_mm,
                p10Mm: data.metadata.wall_thickness.p10_mm,
                medianMm: data.metadata.wall_thickness.median_mm,
                meanMm: data.metadata.wall_thickness.mean_mm,
                maxMm: data.metadata.wall_thickness.max_mm,
                sampleCount: data.metadata.wall_thickness.sample_count || 0,
                hitRate: data.metadata.wall_thickness.hit_rate || 0,
                confidence: data.metadata.wall_thickness.confidence || 'low',
                analyzedBody: data.metadata.wall_thickness.analyzed_body || 'main',
                thinFraction: data.metadata.wall_thickness.thin_fraction,
                hotspots: data.metadata.wall_thickness.hotspots || [],
              }
            : null,
          crossSections: data.metadata.cross_sections
            ? {
                x: data.metadata.cross_sections.x ? {
                  minArea: data.metadata.cross_sections.x.min_area,
                  maxArea: data.metadata.cross_sections.x.max_area,
                  meanArea: data.metadata.cross_sections.x.mean_area,
                  sliceCount: data.metadata.cross_sections.x.slice_count,
                } : null,
                y: data.metadata.cross_sections.y ? {
                  minArea: data.metadata.cross_sections.y.min_area,
                  maxArea: data.metadata.cross_sections.y.max_area,
                  meanArea: data.metadata.cross_sections.y.mean_area,
                  sliceCount: data.metadata.cross_sections.y.slice_count,
                } : null,
                z: data.metadata.cross_sections.z ? {
                  minArea: data.metadata.cross_sections.z.min_area,
                  maxArea: data.metadata.cross_sections.z.max_area,
                  meanArea: data.metadata.cross_sections.z.mean_area,
                  sliceCount: data.metadata.cross_sections.z.slice_count,
                } : null,
              }
            : null,
          meshQuality: data.metadata.mesh_quality
            ? {
                isWatertight: data.metadata.mesh_quality.is_watertight,
                isVolume: data.metadata.mesh_quality.is_volume,
                bodyCount: data.metadata.mesh_quality.body_count,
                degenerateFaces: data.metadata.mesh_quality.degenerate_faces,
                aspectRatioMean: data.metadata.mesh_quality.aspect_ratio_mean,
                aspectRatioMax: data.metadata.mesh_quality.aspect_ratio_max,
              }
            : null,
          draftRisk: data.metadata.draft_risk
            ? {
                pullDirection: data.metadata.draft_risk.pull_direction || [0, 0, 1],
                undercutFraction: data.metadata.draft_risk.undercut_fraction || 0,
                riskRegions: (data.metadata.draft_risk.risk_regions || []).map((r: any) => ({
                  centroid: r.centroid || [0, 0, 0],
                  faceCount: r.face_count || 0,
                  meanAngle: r.mean_angle || 0,
                })),
                confidence: data.metadata.draft_risk.confidence || 'low',
              }
            : null,
        }
      : null,
    aiAnalysis: data.ai_analysis,
    errorMessage: data.error_message,
    errorCode: data.error_code ?? null,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

export function mapThicknessConfig(data: any): ThicknessConfig {
  return {
    lowerMm: Number(data?.lower_mm ?? 0),
    upperMm: Number(data?.upper_mm ?? 0),
    judgmentToleranceMm: Number(data?.judgment_tolerance_mm ?? 0),
    finalLowerMm: Number(data?.final_lower_mm ?? data?.lower_mm ?? 0),
    finalUpperMm: Number(data?.final_upper_mm ?? data?.upper_mm ?? 0),
    clusterRadiusMm: Number(data?.cluster_radius_mm ?? 3),
    clusterMinPoints: Number(data?.cluster_min_points ?? 3),
    profile: (data?.profile || 'standard') as ThicknessAnalysisProfile,
  };
}

export function mapThicknessRegion(data: any): ThicknessRegion {
  return {
    regionId: String(data.region_id),
    judgment: data.judgment,
    kind: data.kind,
    count: Number(data.count || 0),
    measurementIds: data.measurement_ids || [],
    faceIndices: data.face_indices || [],
    centroid: data.centroid || [0, 0, 0],
    extremePoint: data.extreme_point || [0, 0, 0],
    minMm: Number(data.min_mm),
    meanMm: Number(data.mean_mm),
    maxMm: Number(data.max_mm),
    bounds: {
      min: data.bounds?.min || [0, 0, 0],
      max: data.bounds?.max || [0, 0, 0],
    },
    span: data.span || [0, 0, 0],
    sourceFaceIds: data.source_face_ids || [],
    sourceBodyIds: data.source_body_ids || [],
  };
}

export function mapThicknessFaceSummary(data: any): ThicknessFaceSummary {
  return {
    sourceFaceId: String(data.source_face_id),
    sourceBodyId: data.source_body_id || null,
    measurementCount: Number(data.measurement_count || 0),
    measuredCount: Number(data.measured_count || 0),
    unmeasuredCount: Number(data.unmeasured_count || 0),
    minMm: data.min_mm == null ? null : Number(data.min_mm),
    meanMm: data.mean_mm == null ? null : Number(data.mean_mm),
    medianMm: data.median_mm == null ? null : Number(data.median_mm),
    p05Mm: data.p05_mm == null ? null : Number(data.p05_mm),
    p95Mm: data.p95_mm == null ? null : Number(data.p95_mm),
    sampledAreaMm2: Number(data.sampled_area_mm2 || 0),
    rawJudgment: data.raw_judgment,
    finalJudgment: data.final_judgment,
    rawCounts: data.raw_counts || {},
    finalCounts: data.final_counts || {},
  };
}

export function mapThicknessEvidence(data: any): ThicknessEvidence | null {
  if (!data) return null;
  return {
    ...data,
    engineVersion: data.engine_version,
    analysisScope: data.analysis_scope,
    bodyCount: data.body_count,
    meshIsWatertight: data.mesh_is_watertight,
    limitations: data.limitations || [],
  };
}

export function mapThicknessAnalysis(data: any): ThicknessAnalysis {
  const result = data.result
    ? {
        config: mapThicknessConfig(data.result.config),
        quality: data.result.quality,
        qualityReasons: data.result.quality_reasons || [],
        candidateFaceCount: Number(data.result.candidate_face_count || 0),
        excludedFaceCount: Number(data.result.excluded_face_count || 0),
        measuredCount: Number(data.result.measured_count || 0),
        unmeasuredCount: Number(data.result.unmeasured_count || 0),
        rawCounts: data.result.raw_counts || {},
        finalCounts: data.result.final_counts || {},
        toleranceAppliedCount: Number(data.result.tolerance_applied_count || 0),
        reviewRequiredCount: Number(data.result.review_required_count || 0),
        reviewReasonCounts: data.result.review_reason_counts || {},
        isolatedViolationCount: Number(data.result.isolated_violation_count || 0),
        regions: (data.result.regions || []).map(mapThicknessRegion),
        faceSummaries: (data.result.face_summaries || []).map(mapThicknessFaceSummary),
      }
    : null;
  return {
    analysisId: String(data.analysis_id),
    modelId: String(data.model_id),
    status: data.status,
    config: mapThicknessConfig(data.config),
    retryOf: data.retry_of || null,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    completedAt: data.completed_at || null,
    startedAt: data.started_at || null,
    heartbeatAt: data.heartbeat_at || null,
    progressPercent: Number(data.progress_percent || 0),
    attemptCount: Number(data.attempt_count || 0),
    errorMessage: data.error_message || null,
    errorCode: data.error_code ?? null,
    artifacts: data.artifacts || [],
    result,
    evidence: mapThicknessEvidence(data.evidence),
  };
}

export function mapThicknessReview(data: any): ThicknessReview {
  return {
    status: data.status as ThicknessReviewStatus,
    regionDispositions: (data.region_dispositions || {}) as Record<
      string,
      ThicknessRegionDisposition
    >,
    regionReviews: Object.fromEntries(
      Object.entries(data.region_reviews || {}).map(([regionId, value]: [string, any]) => [
        regionId,
        {
          analysisConfirmed: Boolean(value.analysis_confirmed),
          cadVerificationMode: value.cad_verification_mode || 'analysis_value',
          cadMeasuredMm: value.cad_measured_mm ?? null,
          measurementMethod: value.measurement_method || '',
          disposition: value.disposition || 'unreviewed',
          rationale: value.rationale || '',
          assignee: value.assignee || '',
          dueDate: value.due_date || null,
          completedBy: value.completed_by || null,
          completedByName: value.completed_by_name || null,
          completedAt: value.completed_at || null,
        },
      ]),
    ),
    comment: data.comment || '',
    updatedBy: data.updated_by || '',
    updatedByName: data.updated_by_name || '',
    updatedByEmail: data.updated_by_email || '',
    updatedAt: data.updated_at,
    approvedBy: data.approved_by || null,
    approvedAt: data.approved_at || null,
  };
}

export function mapThicknessBatch(data: any): ThicknessBatchJob {
  return {
    jobId: String(data.job_id),
    name: String(data.name || ''),
    status: data.status,
    items: (data.items || []).map((item: any) => ({
      itemId: String(item.item_id),
      filename: String(item.filename || ''),
      modelId: String(item.model_id),
      config: mapThicknessConfig(item.config),
      status: item.status,
      analysisId: item.analysis_id || null,
      errorMessage: item.error_message || null,
      errorCode: item.error_code ?? null,
      judgment: item.judgment || null,
      quality: item.quality || null,
      retryCount: Number(item.retry_count || 0),
    })),
    createdBy: data.created_by || '',
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    progressPercent: Number(data.progress_percent || 0),
    classification: {
      folderPath: String(data.classification?.folder_path || ''),
      tags: Array.isArray(data.classification?.tags)
        ? data.classification.tags.map(String)
        : [],
      machineModel: String(data.classification?.machine_model || ''),
      managementNumber: String(data.classification?.management_number || ''),
    },
    canEditClassification: Boolean(data.can_edit_classification),
  };
}

export function mapThicknessPreset(data: any): ThicknessPreset {
  return {
    presetId: String(data.preset_id),
    name: String(data.name),
    config: mapThicknessConfig(data.config),
    version: Number(data.version || 1),
    createdBy: data.created_by || '',
    supersedes: data.supersedes || null,
    createdAt: data.created_at,
  };
}
